import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import {
  ColDef,
  FilterChangedEvent,
  GridState,
  ModuleRegistry,
  AllCommunityModule,
  SortChangedEvent,
  themeQuartz
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import type { JSX } from 'react';
import { useContext, useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './style.css';

import PageHeader from '@/components/PageHeader';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import ExternalVerificationRepository from '@/repositories/ExternalVerificationRepository';
import { ExternalVerificationSource } from '@/types/externalVerification';
import { InsuranceVerificationListItem } from '@/types/insuranceVerification';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Select from '@/ui/components/Form/Select';
import Spinner from '@/ui/components/Spinner';
import { Paginator } from '@/ui/Paginator/Paginator';
import { PillSwitch, PillSwitchOption } from '@/ui/PillSwitch';
import { resolveGridTheme } from '@/utils/gridTheme';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import {
  BILLING_SUPER_ADMIN_GROUP,
  hasPermission,
  isInGroup,
  PEOPLE_RUN_VOB
} from '@/utils/userPermission';

import {
  COHORT_TOGGLE_COPY,
  EMPTY_COHORT,
  STALE_VOB_DEFAULT_DAYS,
  STALE_WORKLIST_COHORT,
  isStaleWorklistCohort
} from './cohorts';
import type { VobListCohort } from './cohorts';
import CohortToggle from './CohortToggle';
import { createColumnDefs } from './columns';
import { toRequestParams, translateFilterModel, translateSortModel } from './filters';
import type { FilterModel } from './filters';
import LinkFilterChips from './LinkFilterChips';
import {
  buildRequestFilters,
  isNarrowUrlState,
  isVerifiedWithin,
  PICKER_KEYS,
  readVobListUrlState,
  writeVobListUrlState
} from './urlState';
import type { LinkFilters, SortModelItem, VerifiedWithin, VobListUrlState } from './urlState';
import VobListPickers from './VobListPickers';

ModuleRegistry.registerModules([AllCommunityModule]);

const VOB_GRID_THEME = themeQuartz.withParams({ spacing: 5, rowHeight: 40, headerHeight: 44 });

const bigQueryAlert =
  'You are about to send a VERY large request to the database. Are you sure you want to do that?';

const PAGE_SIZE = 100;

const VENDOR_LABELS: Record<ExternalVerificationSource, string> = {
  [ExternalVerificationSource.FRONTRUNNER]: 'FrontRunner',
  [ExternalVerificationSource.STEDI]: 'Stedi'
};

const vendorOptions = Object.values(ExternalVerificationSource).map((source) => ({
  value: source,
  label: VENDOR_LABELS[source]
}));

const isVerificationSource = (value: unknown): value is ExternalVerificationSource =>
  Object.values(ExternalVerificationSource).includes(value as ExternalVerificationSource);

const readMemberIdFromModel = (model: FilterModel): string | undefined => {
  const memberFilter = model.member_id;
  return memberFilter?.type === 'equals' && memberFilter.filter ? memberFilter.filter : undefined;
};

const InsuranceVerificationsList = (): JSX.Element | null => {
  const { addMessage, config, permissions, resolvedTheme, darkStyle } = useContext(AppContext);
  const externalVerificationRepo = ExternalVerificationRepository();
  const gridRef = useRef<AgGridReact>(null);
  const gridTheme = useMemo(
    () => resolveGridTheme(resolvedTheme, darkStyle, VOB_GRID_THEME),
    [resolvedTheme, darkStyle]
  );

  const permissionsLoaded = permissions !== undefined;
  const hasRequiredPermission =
    permissionsLoaded && hasPermission({ requiredPermission: PEOPLE_RUN_VOB, permissions });

  useEffect(() => {
    if (permissionsLoaded && !hasRequiredPermission) {
      addMessage('error', 'You do not have permission to view insurance verifications.');
    }
  }, [permissionsLoaded, hasRequiredPermission, addMessage]);

  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [isRunningVOBs, setIsRunningVOBs] = useState<boolean>(false);
  const [chosenVendor, setChosenVendor] = useState<ExternalVerificationSource | null>(null);
  const configuredVendor = config?.reverification_vendor;
  const vendor =
    chosenVendor ??
    (isVerificationSource(configuredVendor)
      ? configuredVendor
      : ExternalVerificationSource.FRONTRUNNER);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlStateRef = useRef<VobListUrlState>(readVobListUrlState(searchParams));
  const initialFilterModelRef = useRef<FilterModel>(initialUrlStateRef.current.columnFilters);
  const [cohort, setCohort] = useState<VobListCohort>(initialUrlStateRef.current.cohort);
  const [linkFilters, setLinkFilters] = useState<LinkFilters>(
    initialUrlStateRef.current.linkFilters
  );
  const [gridFilterModel, setGridFilterModel] = useState<FilterModel>(
    initialFilterModelRef.current
  );
  const [sortModel, setSortModel] = useState<SortModelItem[]>(initialUrlStateRef.current.sortModel);
  const [page, setPage] = useState<number>(initialUrlStateRef.current.page);
  const [verifiedWithin, setVerifiedWithin] = useState<VerifiedWithin>(
    initialUrlStateRef.current.verifiedWithin
  );

  const popupParent = useMemo<HTMLElement | undefined>(
    () => (typeof document !== 'undefined' ? document.body : undefined),
    []
  );
  const initialGridState = useMemo<GridState>(
    () => ({
      filter: { filterModel: initialFilterModelRef.current },
      sort: { sortModel: initialUrlStateRef.current.sortModel }
    }),
    []
  );

  const requestFilters = useMemo(
    () => ({
      ...buildRequestFilters({ cohort, linkFilters, verifiedWithin }),
      ...translateFilterModel(gridFilterModel)
    }),
    [cohort, linkFilters, verifiedWithin, gridFilterModel]
  );
  const ordering = translateSortModel(sortModel);

  const verificationsQuery = useQuery({
    queryKey: ['insuranceVerificationsList', page, ordering, requestFilters],
    queryFn: () =>
      externalVerificationRepo.listInsuranceVerifications({
        page: String(page),
        size: String(PAGE_SIZE),
        ordering,
        filters:
          Object.keys(requestFilters).length > 0 ? toRequestParams(requestFilters) : undefined
      }),
    enabled: hasRequiredPermission
  });

  useEffect(() => {
    if (!verificationsQuery.isError) return;
    addMessage(mapAxiosErrorMessages(verificationsQuery.error as AxiosError));
  }, [verificationsQuery.isError, verificationsQuery.error, addMessage]);

  const rowData = useMemo(
    () =>
      (verificationsQuery.data?.results ?? []).filter(
        (item: InsuranceVerificationListItem | null): item is InsuranceVerificationListItem =>
          item != null
      ),
    [verificationsQuery.data]
  );
  const totalCount = verificationsQuery.data?.count;
  const totalPages = verificationsQuery.data?.total_pages ?? 1;

  const handleRunSelectedVOBs = useCallback(async () => {
    if (!gridRef.current?.api) return;
    const rows = gridRef.current.api.getSelectedRows() as InsuranceVerificationListItem[];
    const policyIds = Array.from(
      new Set(
        rows
          .map((r) => r.insurance_policy_id)
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
      )
    );
    if (policyIds.length === 0) {
      addMessage('info', 'Select at least one row with a policy to run VOBs.');
      return;
    }
    setIsRunningVOBs(true);
    try {
      await externalVerificationRepo.bulkAutoReverifyPolicies(policyIds, vendor);
      addMessage(
        'success',
        `Queued VOB${policyIds.length === 1 ? '' : 's'} with 🤖 for ${policyIds.length} polic${policyIds.length === 1 ? 'y' : 'ies'} via ${VENDOR_LABELS[vendor]}.`
      );
    } catch (error) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    } finally {
      setIsRunningVOBs(false);
    }
  }, [addMessage, externalVerificationRepo, vendor]);

  const canViewBillingTimeline = isInGroup(permissions, BILLING_SUPER_ADMIN_GROUP);
  const columnDefs: ColDef[] = useMemo(
    () => createColumnDefs({ canViewBillingTimeline }),
    [canViewBillingTimeline]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: false
    }),
    []
  );

  useEffect(() => {
    setSearchParams(
      (previous) =>
        writeVobListUrlState(previous, {
          cohort,
          columnFilters: gridFilterModel,
          linkFilters,
          page,
          sortModel,
          verifiedWithin
        }),
      { replace: true }
    );
  }, [cohort, gridFilterModel, linkFilters, page, sortModel, verifiedWithin, setSearchParams]);

  const applyFilterModel = useCallback((model: FilterModel) => {
    setGridFilterModel(model);
    setPage(1);
    gridRef.current?.api?.setFilterModel(Object.keys(model).length ? model : null);
  }, []);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    if (event.source !== 'columnFilter') return;

    setGridFilterModel((event.api.getFilterModel() || {}) as FilterModel);
    setPage(1);
  }, []);

  const onSortChanged = useCallback((event: SortChangedEvent) => {
    if (event.source !== 'uiColumnSorted') return;

    const next = event.api
      .getColumnState()
      .filter((column) => column.sort === 'asc' || column.sort === 'desc')
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((column) => ({ colId: column.colId, sort: column.sort as 'asc' | 'desc' }));

    setSortModel(next);
    setPage(1);
  }, []);

  const applyCohort = useCallback(
    (next: VobListCohort) => {
      const isStartingStale = next.staleDays != null && cohort.staleDays == null;
      const isEndingStale = next.staleDays == null && cohort.staleDays != null;
      if (isStartingStale) setVerifiedWithin('all');
      if (isEndingStale) setVerifiedWithin('6m');

      setCohort(next);
      setPage(1);
    },
    [cohort.staleDays]
  );

  const changeVerifiedWithin = useCallback(
    (next: VerifiedWithin) => {
      const isNarrow = isNarrowUrlState({
        cohort,
        memberId: readMemberIdFromModel(gridFilterModel),
        linkFilters
      });
      if (next === 'all' && !isNarrow && !window.confirm(bigQueryAlert)) return;

      setVerifiedWithin(next);
      setPage(1);
    },
    [cohort, gridFilterModel, linkFilters]
  );

  const setPickerValues = useCallback(
    (key: string, values: string[]) => {
      const next = Object.fromEntries(
        Object.entries(linkFilters).filter(([linkKey]) => linkKey !== key)
      );
      setLinkFilters(values.length ? { ...next, [key]: values } : next);
      setPage(1);
    },
    [linkFilters]
  );

  const chipFilters = Object.fromEntries(
    Object.entries(linkFilters).filter(([key]) => !PICKER_KEYS.includes(key))
  );

  const removeLinkFilter = useCallback(
    (key: string) => {
      const next = Object.fromEntries(
        Object.entries(linkFilters).filter(([linkKey]) => linkKey !== key)
      );
      setLinkFilters(next);
      setPage(1);
    },
    [linkFilters]
  );

  const clearAllFilters = useCallback(() => {
    setCohort(EMPTY_COHORT);
    setLinkFilters({});
    setVerifiedWithin('6m');
    applyFilterModel({});
  }, [applyFilterModel]);

  const linkedPolicyCount = (linkFilters.policy_id_in ?? [])
    .flatMap((value) => value.split(','))
    .filter((id) => id.trim()).length;
  const isShowingPartOfWorkList =
    cohort.isMostRecentPerPolicy &&
    linkedPolicyCount > 0 &&
    totalCount !== undefined &&
    totalCount < linkedPolicyCount;

  if (!permissionsLoaded) {
    return (
      <Container type="page">
        <PageHeader headingText="VOB Tool" />
        <Spinner />
      </Container>
    );
  }

  if (!hasRequiredPermission) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="w-full">
        <div className="mb-4">
          <PageHeader headingText="VOB Tool" />
        </div>

        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-4 border-b border-gray-100 pb-3 mb-3">
              <Button
                ariaLabel="Stale VOBs"
                className={
                  isStaleWorklistCohort(cohort)
                    ? 'bg-blue-100 hover:bg-blue-200 text-blue-900 border border-blue-300 ring-1 ring-blue-300'
                    : 'bg-white hover:bg-blue-50 text-blue-700 border border-blue-200'
                }
                color={Color.WHITE}
                size="base"
                onClick={() =>
                  applyCohort(isStaleWorklistCohort(cohort) ? EMPTY_COHORT : STALE_WORKLIST_COHORT)
                }
                title="Treatable, active members whose latest VOB per policy is over 30 days old. Excludes lapsed and deleted policies, and anyone kept out of analytics.">
                Stale VOBs
              </Button>
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.mostRecentPerPolicy}
                checked={cohort.isMostRecentPerPolicy}
                onChange={(checked) => applyCohort({ ...cohort, isMostRecentPerPolicy: checked })}
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.stale}
                checked={cohort.staleDays != null}
                onChange={(checked) =>
                  applyCohort({
                    ...cohort,
                    staleDays: checked ? STALE_VOB_DEFAULT_DAYS : undefined
                  })
                }
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.treatable}
                checked={cohort.isTreatable}
                onChange={(checked) => applyCohort({ ...cohort, isTreatable: checked })}
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.activeMember}
                checked={cohort.isActiveMember}
                onChange={(checked) => applyCohort({ ...cohort, isActiveMember: checked })}
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.activePolicy}
                checked={cohort.isActivePolicy}
                onChange={(checked) => applyCohort({ ...cohort, isActivePolicy: checked })}
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.includeDeleted}
                checked={cohort.includeDeletedPolicies}
                color="destructive"
                triggerClassName="inline-flex border-l border-gray-200 pl-4"
                onChange={(checked) => applyCohort({ ...cohort, includeDeletedPolicies: checked })}
              />
              <CohortToggle
                copy={COHORT_TOGGLE_COPY.includeExcludedFromAnalyticsMembers}
                checked={cohort.includeExcludedFromAnalyticsMembers}
                color="destructive"
                onChange={(checked) =>
                  applyCohort({ ...cohort, includeExcludedFromAnalyticsMembers: checked })
                }
              />
              <LinkFilterChips linkFilters={chipFilters} onRemove={removeLinkFilter} />
            </div>
            <div className="flex flex-wrap items-center gap-4 border-b border-gray-100 pb-3 mb-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Verified</span>
                <PillSwitch
                  value={verifiedWithin}
                  size="sm"
                  onChange={(value) => {
                    if (isVerifiedWithin(value)) changeVerifiedWithin(value);
                  }}>
                  <PillSwitchOption value="today">Today</PillSwitchOption>
                  <PillSwitchOption value="6m">Last 6 months</PillSwitchOption>
                  <PillSwitchOption value="1y">Last year</PillSwitchOption>
                  <PillSwitchOption value="all">All time</PillSwitchOption>
                </PillSwitch>
              </div>
              <VobListPickers linkFilters={linkFilters} onChange={setPickerValues} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-semibold">{totalCount ?? '—'}</span>
                </div>
                {isShowingPartOfWorkList && (
                  <div className="text-sm text-amber-700">
                    Showing {totalCount} of {linkedPolicyCount} linked policies. The rest are hidden
                    by the filters above or have no VOB yet.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  ariaLabel="Clear all filters"
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800"
                  color={Color.WHITE}
                  size="base"
                  onClick={clearAllFilters}
                  title="Clear all filters">
                  Clear Filters
                </Button>
                <div className="w-72">
                  <Select
                    inline
                    label="Vendor:"
                    options={vendorOptions}
                    selectedOption={
                      vendorOptions.find((opt) => opt.value === vendor) ?? vendorOptions[0]
                    }
                    onChange={(option) => {
                      if (isVerificationSource(option.value)) setChosenVendor(option.value);
                    }}
                  />
                </div>
                <Button
                  ariaLabel="Run VOBs for selected"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  color={Color.INDIGO}
                  size="base"
                  disabled={selectedCount === 0 || isRunningVOBs}
                  onClick={handleRunSelectedVOBs}
                  title="Run VOBs for selected policies">
                  <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">
                    <FontAwesomeIcon icon={faRobot} />
                  </span>
                  Run VOBs ({selectedCount})
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full shadow-lg border border-gray-200 rounded-lg overflow-hidden">
          <div className="w-full ins-verifications-grid">
            <AgGridReact
              ref={gridRef}
              theme={gridTheme}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableBrowserTooltips={true}
              popupParent={popupParent}
              initialState={initialGridState}
              rowData={rowData}
              loading={verificationsQuery.isFetching}
              getRowId={(params) => params.data.id}
              onSortChanged={onSortChanged}
              onFilterChanged={onFilterChanged}
              onSelectionChanged={() => {
                const count = gridRef.current?.api.getSelectedRows().length ?? 0;
                setSelectedCount(count);
              }}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: true,
                enableClickSelection: true
              }}
              rowBuffer={10}
              enableCellTextSelection={true}
              domLayout="normal"
              suppressHorizontalScroll={false}
              maintainColumnOrder={true}
            />
          </div>
          <div className="flex items-center justify-end px-5 py-3 border-t border-gray-200 bg-white">
            <Paginator currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsuranceVerificationsList;
